import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepIcon,
  Button,
  Tooltip,
  Badge,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useOrder } from "./useOrder";
import CopyClipboardWrap from "src/components/UI/CopyClipboardWrap";
import toast from "react-hot-toast";

const ORDER_STATUS_CONFIG = {
  PAYMENT_WAIT: {
    label: "Ожидание платежа",
    color: "warning",
    progress: 10,
    step: 0,
  },
  PAYMENT_PROCESS: {
    label: "Обработка платежа",
    color: "info",
    progress: 30,
    step: 1,
  },
  PAYMENT_FAIL: {
    label: "Ошибка платежа",
    color: "error",
    progress: 0,
    step: 0,
  },
  PAYMENT_FINISHED: {
    label: "Платеж завершен",
    color: "success",
    progress: 50,
    step: 2,
  },
  PAYMENT_PARTIALLY_FINISHED: {
    label: "Частичный платеж",
    color: "warning",
    progress: 40,
    step: 1,
  },
  CONVERSION_PROCESS: {
    label: "Обработка конвертации",
    color: "info",
    progress: 60,
    step: 2,
  },
  CONVERSION_FINISHED: {
    label: "Конвертация завершена",
    color: "success",
    progress: 65,
    step: 2,
  },
  CONVERSION_FAIL: {
    label: "Ошибка конвертации",
    color: "error",
    progress: 0,
    step: 2,
  },
  PAYOUT_PROCESS: {
    label: "Обработка выплаты",
    color: "info",
    progress: 70,
    step: 3,
  },
  PAYOUT_FAIL: {
    label: "Ошибка выплаты",
    color: "error",
    progress: 0,
    step: 2,
  },
  PAYOUT_FINISHED: {
    label: "Заявка завершена",
    color: "success",
    progress: 100,
    step: 4,
  },
};

const STEPS = [
  "Ожидание платежа",
  "Обработка платежа",
  "Обработка конвертации",
  "Конвертация завершена",
  "Обработка выплаты",
  "Заявка завершена",
];

// Компонент бейджа со справкой
function HelpBadge() {
  const [showBadge, setShowBadge] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    // Проверяем, была ли справка уже просмотрена
    const hasSeenHelp = localStorage.getItem("crypto-auto-change-help-seen");
    const targetDate = new Date("2025-07-25");
    const currentDate = new Date();

    // Показываем бейдж только если не просмотрено и дата не истекла
    if (!hasSeenHelp && currentDate < targetDate) {
      setShowBadge(true);
    }
  }, []);

  const handleTooltipClose = () => {
    setShowTooltip(false);

    // Сохраняем информацию о том, что справка была просмотрена
  };

  const handleIsOk = () => {
    localStorage.setItem("crypto-auto-change-help-seen", "true");
    setShowBadge(false);
    handleTooltipClose();
  };

  // if (!showBadge) return null;

  return (
    <Tooltip
      open={showTooltip}
      onClose={handleTooltipClose}
      title={
        <Card sx={{ p: 2, maxWidth: 300 }}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: "bold" }}>
            Справка по автообмену
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Автоматический обмен криптовалют происходит без участия оператора.
            Система автоматически отслеживает поступление средств и изменяет
            заявку.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Если остановить Автообмен, то средства клиенту не будут переведены.
            Также эта карточка будет менять свой статус (так как возможно клиент
            отправил средства, и транзакция в обработке) для того чтобы
            информировать о поступлении оператора. А сама заявка нашего
            обменника не будет меняться автоматически.
          </Typography>
          <Button
            size="small"
            variant="contained"
            onClick={handleIsOk}
            sx={{ fontSize: "0.75rem" }}
          >
            Понятно
          </Button>
        </Card>
      }
      arrow
      placement="top"
      PopperProps={{
        sx: {
          "& .MuiTooltip-tooltip": {
            // backgroundColor: "rgba(0, 0, 0, 0.9)",
            color: "white",
            fontSize: "0.875rem",
            maxWidth: 320,
            p: 0,
          },
        },
      }}
    >
      <Badge
        sx={{
          cursor: "pointer",
        }}
        onClick={() => setShowTooltip(true)}
      >
        <Icon icon="mdi:help-circle" color="#1976d2" width={24} height={24} />
      </Badge>
    </Tooltip>
  );
}

// Кастомная иконка для шага
function CustomStepIcon({
  active,
  completed,
  currentStep,
  stepIndex,
  isFinished,
  isStopped,
}) {
  // Если заявка полностью завершена, показываем галочки для всех шагов
  if (isFinished) {
    return (
      <Icon icon="mdi:check-circle" color="#4caf50" width={24} height={24} />
    );
  }

  // Если заявка остановлена
  if (isStopped) {
    // Завершенные этапы показываем с галочкой
    if (completed) {
      return (
        <Icon icon="mdi:check-circle" color="#4caf50" width={24} height={24} />
      );
    }
    // Незавершенные этапы показываем с паузой
    return (
      <Icon icon="mdi:pause-circle" color="#ff9800" width={24} height={24} />
    );
  }

  if (completed) {
    return (
      <Icon icon="mdi:check-circle" color="#4caf50" width={24} height={24} />
    );
  }

  if (active && currentStep === stepIndex) {
    return (
      <Box
        sx={{
          animation: "spin 1s linear infinite",
          "@keyframes spin": {
            "0%": {
              transform: "rotate(0deg)",
            },
            "100%": {
              transform: "rotate(360deg)",
            },
          },
        }}
      >
        <Icon icon="mdi:loading" color="#1976d2" width={24} height={24} />
      </Box>
    );
  }

  return (
    <Icon icon="mdi:radiobox-blank" color="#9e9e9e" width={24} height={24} />
  );
}

function AutoCryptoChangeProgressCard({ orderId }) {
  const [isFetch, setIsFetch] = useState("");
  const { order, loading, error, refetch, emitOrder } = useOrder(orderId);

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Загрузка заявки...
          </Typography>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error" onClose={refetch}>
            Ошибка загрузки: {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!order) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="textSecondary">
            Заявка не найдена
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const stopHandler = () => {
    if (!window.confirm("Остановить авто обмен и перейти к ручному режиму?"))
      return;
    setIsFetch("stop");
    emitOrder(orderId, {
      code: "stop",
    })
      .then(() => {
        toast.success(
          "Авто обмен остановлен и заявка переведена в ручной режим! Средства не будут переведены клиенту."
        );
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message);
      })
      .finally(() => {
        setIsFetch("");
      });
  };

  const statusConfig = ORDER_STATUS_CONFIG[order.status] || {
    label: "Неизвестный статус",
    color: "default",
    progress: 0,
    step: 0,
  };

  const currentStep = statusConfig.step;
  const isError = order.status.includes("FAIL");
  const isFinished = order.status === "PAYOUT_FINISHED";
  const isStopped = order.is_stopped;

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Box display="flex" gap={1} alignItems="center">
            {/* <Typography variant="h6">Заявка #{order.id}</Typography> */}
            <HelpBadge />
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <Chip
              label={statusConfig.label}
              color={statusConfig.color}
              variant="outlined"
            />
            {isStopped ? (
              <Chip
                label="Остановлена"
                color="warning"
                variant="filled"
                icon={<Icon icon="mdi:pause-circle" />}
              />
            ) : null}
          </Box>
        </Box>

        <Box mb={3}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Прогресс выполнения
          </Typography>
          <Box
            sx={{
              overflowX: "auto",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Stepper
              activeStep={isStopped ? -1 : currentStep}
              alternativeLabel
              sx={{ minWidth: 600 }}
            >
              {STEPS.map((label, index) => (
                <Step key={label}>
                  <StepLabel
                    StepIconComponent={(props) => (
                      <CustomStepIcon
                        {...props}
                        currentStep={currentStep}
                        stepIndex={index}
                        completed={isFinished ? true : index < currentStep}
                        active={
                          isStopped
                            ? false
                            : isFinished
                            ? false
                            : index === currentStep
                        }
                        isFinished={isFinished}
                        isStopped={isStopped}
                      />
                    )}
                  >
                    <Typography
                      variant="caption"
                      color={
                        isFinished || index <= currentStep
                          ? "textPrimary"
                          : "textSecondary"
                      }
                      sx={{
                        fontWeight:
                          isFinished || index === currentStep
                            ? "bold"
                            : "normal",
                      }}
                    >
                      {label}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Адрес для оплаты
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ wordBreak: "break-all", mb: 1, flex: 1 }}
              >
                {order.from_address}
              </Typography>
              <CopyClipboardWrap value={order.from_address} size="large">
                <Box />
              </CopyClipboardWrap>
            </Box>
            <Typography variant="body1">
              {order.from_amount} {order.from_currency}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
              Адрес для выплаты
            </Typography>
            <Box display="flex" alignItems="center">
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ wordBreak: "break-all", mb: 1, flex: 1 }}
              >
                {order.to_address}
              </Typography>
              <CopyClipboardWrap value={order.to_address} size="large">
                <Box />
              </CopyClipboardWrap>
            </Box>
            <Typography variant="body1">
              {order.to_amount} {order.to_currency}
            </Typography>
          </Box>
        </Box>

        {order.payment_error_message && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Ошибка платежа: {order.payment_error_message}
          </Alert>
        )}

        {order.payout_error_message && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Ошибка выплаты: {order.payout_error_message}
          </Alert>
        )}

        {/* Кнопки управления для заявок, которые не достигли статуса выплаты */}
        {order.status !== "PAYOUT_PROCESS" &&
          order.status !== "PAYOUT_FINISHED" &&
          !isStopped && (
            <Box display="flex" gap={2} mt={3}>
              <Button
                variant="outlined"
                onClick={stopHandler}
                disabled={isFetch === "stop"}
                color="warning"
                fullWidth
                startIcon={<Icon icon="mdi:pause-circle" />}
              >
                остановить и перейти в ручной режим
              </Button>
            </Box>
          )}
      </CardContent>
    </Card>
  );
}

export default AutoCryptoChangeProgressCard;
